// src/configs/ConfigIndex.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import 'dayjs/locale/pt';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import 'dayjs/locale/it';
import 'dayjs/locale/ja';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import weekday from 'dayjs/plugin/weekday';
import isBetween from 'dayjs/plugin/isBetween';
import localeData from 'dayjs/plugin/localeData';
import relativeTime from 'dayjs/plugin/relativeTime';

import { Navigate } from 'react-router-dom';
import NoComponentSet from 'components/JarvislyModule/NoComponentSet';

import app from './appResolver';
import { baseTheme, MODULES } from './appEngine';
import apiConfig from 'configs/fetchInterceptor';
import {
  DIR_LTR,
  NAV_TYPE_SIDE,
  NAV_TYPE_TILE,
  NAV_TYPE_TOP,
  SIDE_NAV_LIGHT,
  THEME_COLOR,
} from 'constants/ThemeConstant';

import { fromAppStore } from 'stores/app';
import { fromAuthStore } from 'stores/auth';
import { fromBootStore } from 'stores/boot';
import { fromGlobalStore } from 'stores/global';
import { fromModuleStore } from 'stores/module';
import { fromThemeStore } from 'stores/theme';
import { fromTourStore } from 'stores/tour';

import withModuleSetup from 'hoc/withModuleSetup';
import useZustand from 'hooks/useZustand';
import { clone } from 'utils/helpers';
import consoleX from 'utils/consoleX';
import { registerCustomLocales } from 'utils/dayjsCustomLocales';
import { generateRegistrationLink } from 'utils/crypto';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

const inMaintenance = []; // e.g., ['sindico', 'meucondominio']

// *******************************************************************************
// DAYJS CONFIGURATION
// *******************************************************************************

dayjs.extend(localeData);
dayjs.extend(isBetween);
dayjs.extend(localizedFormat);
dayjs.extend(weekday);
dayjs.extend(relativeTime);

registerCustomLocales();
dayjs.locale(mapToDayjsLocale(app.locale));

// *******************************************************************************
// APP CONFIGURATION
// *******************************************************************************

app.IN_MAINTENANCE = inMaintenance.includes(app.ID);

const navType = app?.NAVIGATION_TYPE;
const initialNavType =
  navType === 'tile' ? NAV_TYPE_TILE : navType === 'top' ? NAV_TYPE_TOP : NAV_TYPE_SIDE;

app.navCollapsed = false;
app.sideNavTheme = SIDE_NAV_LIGHT;
app.navType = initialNavType;
app.topNavColor = baseTheme.colorPrimary;
app.headerNavColor = '';
app.mobileNav = false;
app.direction = DIR_LTR;
app.blankLayout = false;
app.selectedRoute = null;
app.backgroundColor = app.currentTheme === 'dark' ? '#283142' : '#ffffff';
app.baseColor = THEME_COLOR[app.THEME_COLOR];

app.appRoutes = {};
app.routeResources = {}; // Map of route path → requiredResources[] for access control
app.storedAppModules = [];
app.appModulesMap = {};
app.BASE_THEME = baseTheme;

getRoutes(app.MENU_NAVIGATION);
getRoutes(app.MENU_APPLICATION);

// *******************************************************************************
// INTERNAL FUNCTIONS
// *******************************************************************************

function getRoutes(menuArr, parentPath = '', parentRequiredResources = []) {
  const setAppModulesStore = useZustand('app', 'setAppModules', false);
  const storedAppModules = useZustand('app', 'appModules', false);

  menuArr?.forEach(menu => {
    // Merge parent required resources with menu's own required resources
    // If menu has its own requiredResources, use those; otherwise inherit from parent
    const effectiveRequiredResources = menu.requiredResources?.length
      ? menu.requiredResources
      : parentRequiredResources;

    // For sections with submenu, pass down the section path and required resources
    if (menu?.submenu?.length > 0) {
      const sectionPath = menu.path || parentPath;
      // Pass the group's requiredResources to children
      const groupResources = menu.requiredResources || parentRequiredResources;
      getRoutes(menu.submenu, sectionPath, groupResources);
      return;
    }

    // Calculate menu.path if not provided (new feature)
    // Retrocompatible: if path exists, use it; otherwise calculate from moduleId
    let menuPath = menu.path;
    if (!menuPath && menu?.moduleId && parentPath) {
      // Convert camelCase moduleId to kebab-case for URL
      const kebabModuleId = camelToKebab(menu.moduleId);
      menuPath = `${parentPath}/${kebabModuleId}`;
      // IMPORTANT: Mutate the menu object so MenuContent.jsx can access the calculated path
      menu.path = menuPath;
    }

    // Only visible items with known moduleId
    if (
      !menu?.hide &&
      menu?.moduleId &&
      (MODULES[menu.moduleId]?.CONFIG || MODULES[menu.moduleId])
    ) {
      const C = MODULES[menu.moduleId]?.CONFIG;
      const M = C || MODULES[menu.moduleId];
      const P = MODULES[menu.moduleId]?.PAGES || {};
      const MOCK = MODULES[menu.moduleId]?.MOCK || {};

      // Get the main page from module CONFIG (default: 'init')
      // 'initialPage' in CONFIG defines which PAGES key becomes the base URL (endpoint: null)
      // Fallback to 'init' if not defined in CONFIG
      const initPage = C?.initialPage || M?.initialPage || 'init';

      // Decorate module
      M.url = menuPath;
      M.initPage = initPage;
      M.initialParams = C?.initialParams || null;
      M.initialQuery = C?.initialQuery || null;
      M.title = menu.title;
      M.icon = menu.icon;
      M.routes = [];

      const finalModule = { ...M, pages: { ...P } };

      // 1) compat (array)
      if (!app.storedAppModules.find(m => m.id === finalModule.id)) {
        app.storedAppModules.push(finalModule);
      }

      // 2) mapa paralelo
      app.appModulesMap[M.id] = finalModule;
      storedAppModules[M.id] = { pages: { ...P } };
      setAppModulesStore({ ...storedAppModules });

      Object.keys(P).forEach(k => {
        const page = P[k];
        page.name = k;
        page.mock = MOCK[k] || {};

        // Main page (initPage) always maps to the base URL
        // Other pages use their routing.endpoint, or auto-generated /{key} if null
        let route;
        if (k === initPage) {
          // This is the main page - override endpoint to base URL
          // UNLESS the endpoint has dynamic params (e.g., /:userId) - preserve those
          if (page?.routing?.endpoint && page.routing.endpoint.includes(':')) {
            route = `${M.url}${page.routing.endpoint}`;
          } else {
            route = M.url;
            if (page.routing) page.routing.endpoint = null;
          }
        } else if (page?.routing?.endpoint) {
          route = `${M.url}${page.routing.endpoint}`;
        } else {
          // Non-main page without endpoint - auto-generate from page key
          const autoEndpoint = `/${k}`;
          route = `${M.url}${autoEndpoint}`;
          if (page.routing) page.routing.endpoint = autoEndpoint;
        }

        const component = page?.component
          ? withModuleSetup(page.component, k, C)
          : withModuleSetup(NoComponentSet, k, C, true);

        if (menu?.component) {
          app.appRoutes[menuPath] = menu.component;
          app.routeResources[menuPath] = effectiveRequiredResources;
          M.routes = [menuPath];
        } else {
          app.appRoutes[route] = component;
          app.routeResources[route] = effectiveRequiredResources;
          M.routes.push(route);
        }

        if (page?.component) delete page.component;
      });

      // Resolve initial navigation from CONFIG.initialParams + initialQuery
      // When initPage has dynamic endpoint (e.g., /:_id), the base URL has no route.
      // We register a redirect route so both menu clicks and direct URL access work.
      const initialParams = C?.initialParams;
      const initialQuery = C?.initialQuery;
      const initPageRouting = P[initPage]?.routing;
      const hasDynamicInitPage =
        initPageRouting?.endpoint && initPageRouting.endpoint.includes(':');

      if (hasDynamicInitPage && initialParams) {
        let resolvedEndpoint = initPageRouting.endpoint;
        Object.entries(initialParams).forEach(([key, value]) => {
          resolvedEndpoint = resolvedEndpoint.replace(`:${key}`, value);
        });

        // Only create redirect if all placeholders were resolved (non-matching ignored)
        if (!resolvedEndpoint.includes(':')) {
          const queryStr =
            initialQuery && Object.keys(initialQuery).length > 0
              ? `?${new URLSearchParams(initialQuery).toString()}`
              : '';
          const targetPath = `${menuPath}${resolvedEndpoint}${queryStr}`;

          // 1. Register redirect route for the base URL (handles direct access & menu)
          const RedirectToInitial = () => <Navigate to={targetPath} replace />;
          app.appRoutes[menuPath] = RedirectToInitial;
          app.routeResources[menuPath] = effectiveRequiredResources;

          // 2. Store resolved path for menu navigation (avoids redirect hop)
          if (!app.initialNavPaths) app.initialNavPaths = {};
          app.initialNavPaths[menuPath] = `${menuPath}${resolvedEndpoint}`;

          if (initialQuery && Object.keys(initialQuery).length > 0) {
            if (!app.initialNavQueries) app.initialNavQueries = {};
            app.initialNavQueries[menuPath] = new URLSearchParams(initialQuery).toString();
          }
        }
      }
    } else {
      // Route without component (placeholder) - no resource check so NoRouteSet can render
      if (!menu?.hide && menuPath) {
        app.appRoutes[menuPath] = null;
        app.routeResources[menuPath] = [];
      }
    }
  });
}

if (!Array.isArray(app.storedAppModules)) {
  app.storedAppModules = Object.values(app.storedAppModules || {});
}

// *******************************************************************************
// EXPORTED FUNCTIONS
// *******************************************************************************

const buildMarketplace = availableModules => {
  availableModules?.forEach(m => {
    if (m?.routes) {
      const r = clone(m.routes);
      // strings: sort deterministically (alphabetical desc)
      m.routes = r.sort((a, b) => (a > b ? -1 : 1));
    }

    if (!m.marketplace) return m;

    switch (m.status) {
      case 0:
        m.__status = 'not_contracted';
        m.__statusColor = 'gold';
        break;
      case 1:
        m.__status = 'waiting_for_payment';
        m.__statusColor = 'purple';
        break;
      case 2:
      case 3:
        m.__status = 'evaluation_period';
        m.__statusColor = 'cyan';
        break;
      case -2:
      case -3:
        m.__status = 'evaluation_period_expired';
        m.__statusColor = 'volcano';
        break;
      case -4:
        if (m.__leftDays > 0) {
          m.__status = 'active_until';
          m.__statusColor = 'lime';
        } else {
          m.__status = 'not_contracted';
          m.__statusColor = 'gold';
        }
        break;
      default:
        m.__status = 'not_contracted';
        m.__statusColor = 'volcano';
        break;
    }

    return m;
  });
};

const buildRoutes = availableModules => {
  const setRoutesStore = useZustand('app', 'setRoutes', false);
  let routes = [];

  availableModules?.forEach(m => {
    m?.routes?.forEach(r => {
      if (!routes.includes(r)) routes.push(r);
    });
  });

  setRoutesStore(routes);
  return routes.sort((a, b) => b.length - a.length);
};

export function buildAppConfigurations(subscription) {
  const setNavigationMenuStore = useZustand('app', 'setNavigationMenu', false);
  const availableModules = buildModules(subscription);
  const navigateMenu = app?.MENU_NAVIGATION || [];
  const navigateApplication = app?.MENU_APPLICATION || [];
  const routes = buildRoutes(availableModules);

  // Expose subscription country for locale-aware components (SimpleCurrency, SimpleDatePicker, etc.)
  // buildRequestLocaleHeaders() reads app.subscriptionCountryCode for dynamic x-locale header
  app.subscriptionCountryCode = subscription?.countryCode?.toUpperCase() || null;

  setNavigationMenuStore([...navigateMenu, ...navigateApplication]);

  app.routes = routes;
  buildMarketplace(availableModules);

  return {
    availableModules,
    navigateMenu,
    routes,
  };
}

const buildModules = subscription => {
  const MKT_MODULES = subscription?.marketplace || [];

  if (!subscription) {
    return clone(app.storedAppModules?.filter(m => m.id === 'account' || m.id === 'welcome'));
  } else {
    return app.storedAppModules
      .map(m => {
        const mktModule = MKT_MODULES.find(i => i.id === m.id);
        return mktModule ? { ...m, ...mktModule } : { ...m };
      })
      .filter(Boolean);
  }
};

export const printGlobalStateToConsole = () => {
  // Captura o form antes de imprimir (se existe)
  if (typeof window.__CAPTURE_FORM_FOR_DEBUG__ === 'function') {
    window.__CAPTURE_FORM_FOR_DEBUG__();
  }

  const zAuth = fromAuthStore();
  const zApp = fromAppStore();
  const zModule = fromModuleStore();
  const zBoot = fromBootStore();
  const zTheme = fromThemeStore();
  const zTour = fromTourStore();
  const zGlobal = fromGlobalStore();


  consoleX({
    message: 'Module',
    emoji: '[pkg]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      store: 'module',
      config: zModule?.config,
      Payload: JSON.stringify(zModule?.config, null, 2),
    },
  });

  consoleX({
    message: 'Page',
    emoji: '[doc]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      store: 'module',
      currentPage: zModule?.currentPage,
      Payload: JSON.stringify(zModule?.page, null, 2),
    },
  });

  consoleX({
    message: 'Data',
    emoji: '[data]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      store: 'module',
      'currentPage.data': zModule?.currentPage?.data,
      Payload: JSON.stringify(zModule?.currentPage?.data, null, 2),
    },
  });

  consoleX({
    message: 'Form (Debug)',
    emoji: '[form]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      store: 'module',
      formDebug: zModule?.formDebug,
      Payload: JSON.stringify(zModule?.formDebug, null, 2),
    },
  });

  consoleX({
    message: 'User',
    emoji: '[user]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      store: 'auth',
      user: zAuth?.user,
      Payload: JSON.stringify(zAuth?.user, null, 2),
    },
  });

  consoleX({
    message: 'Subscriptions',
    emoji: '[web]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      store: 'auth',
      subscriptions: zAuth?.subscriptions,
      Payload: JSON.stringify(zAuth?.subscriptions, null, 2),
    },
  });

  consoleX({
    message: 'Subscription',
    emoji: '[sub]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      store: 'auth',
      subscription: zAuth?.subscription,
      Payload: JSON.stringify(zAuth?.subscription, null, 2),
    },
  });

  consoleX({
    message: 'Companies',
    emoji: '[factory]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      store: 'auth',
      companies: zAuth?.companies,
      Payload: JSON.stringify(zAuth?.companies, null, 2),
    },
  });

  consoleX({
    message: 'Company',
    emoji: '[pin]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      store: 'auth',
      company: zAuth?.company,
      Payload: JSON.stringify(zAuth?.company, null, 2),
    },
  });

  consoleX({
    message: 'genericPerson',
    emoji: '[user]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      store: 'auth',
      me: zAuth?.genericPerson,
      Payload: JSON.stringify(zAuth?.genericPerson, null, 2),
    },
  });

  consoleX({
    message: 'genericCompany',
    emoji: '[bldg]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      store: 'auth',
      me: zAuth?.genericCompany,
      Payload: JSON.stringify(zAuth?.genericCompany, null, 2),
    },
  });

  consoleX({
    message: 'Team',
    emoji: '[team]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      store: 'auth',
      team: zAuth?.team,
      Payload: JSON.stringify(zAuth?.team, null, 2),
    },
  });

  consoleX({
    message: 'Me',
    emoji: '[me]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      store: 'auth',
      me: zAuth?.me,
      Payload: JSON.stringify(zAuth?.me, null, 2),
    },
  });


  consoleX({
    message: 'Parameters',
    emoji: '[cfg]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      store: 'app',
      parameters: zApp?.parameters,
      Payload: JSON.stringify(zApp?.parameters, null, 2),
    },
  });

  consoleX({
    message: 'ModuleContext',
    emoji: '[cfg]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      store: 'global',
      moduleContext: zGlobal.moduleContext,
      Payload: JSON.stringify(zGlobal.moduleContext, null, 2),
    },
  });

  consoleX({
    message: 'ListContext',
    emoji: '[cfg]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      store: 'global',
      listContext: zGlobal.listContext,
      Payload: JSON.stringify(zGlobal.listContext, null, 2),
    },
  });

  consoleX({
    message: 'ProviderContexts',
    emoji: '[link]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      store: 'global',
      providerContexts: zGlobal.providerContexts,
      listContext: zGlobal.providerContexts?.listContext,
      documentContext: zGlobal.providerContexts?.documentContext,
      formContext: zGlobal.providerContexts?.formContext,
      Payload: JSON.stringify(zGlobal.providerContexts, null, 2),
    },
  });

  consoleX({
    message: 'DocumentContext',
    emoji: '[cfg]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      store: 'global',
      documentContext: zGlobal.documentContext,
      Payload: JSON.stringify(zGlobal.documentContext, null, 2),
    },
  });

  consoleX({
    message: 'FormContext',
    emoji: '[cfg]',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      origin: 'global',
      formContext: zGlobal.formContext,
      Payload: JSON.stringify(zGlobal.formContext, null, 2),
    },
  });

  consoleX({
    message: 'Headers',
    emoji: '[cfg]',
    flag: 'default',
    title: 'HTTP HEADERS',
    info: {
      origin: 'apiConfig.defaults.headers',
      Data: apiConfig.defaults.headers,
      Payload: JSON.stringify(apiConfig.defaults.headers, null, 2),
    },
  });

  consoleX({
    message: 'All Stores',
    emoji: '[cfg]',
    color: '#00bcd4',
    flag: 'progress',
    title: 'ZUSTAND STATE',
    info: {
      app: zApp,
      auth: zAuth,
      boot: zBoot,
      module: zModule,
      theme: zTheme,
      tour: zTour,
      global: zGlobal,
    },
  });

  consoleX({
    message: 'Registration Link Generator',
    emoji: '[key]',
    color: '#fa2222',
    flag: 'progress',
    title: 'SECURITY',
    input: {
      name: 'signup',
      prompt: 'Enter email for registration link:',
      callback: async email => {
        const result = await generateRegistrationLink({ email });
        return result;
      },
    },
  });

  consoleX({
    message: 'UUID Generator',
    emoji: '[num]',
    color: '#7c3aed',
    flag: 'progress',
    title: 'UTILS',
    input: {
      name: 'generateCode',
      noPrompt: true,
      callback: () => {
        const uuid = crypto.randomUUID();
        return uuid;
      },
    },
  });

};

// *******************************************************************************
// EXPORTS
// *******************************************************************************

export default app;

// *******************************************************************************
// HELPERS
// *******************************************************************************

function camelToKebab(str) {
  // Convert camelCase to kebab-case: dataSearch -> data-search
  return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

function mapToDayjsLocale(appLocale) {
  // Map common i18n codes used by the app to Dayjs locale ids
  const lc = String(appLocale || 'en-us').toLowerCase();

  // English variants map to 'en'
  if (['en', 'en-us', 'en-gb', 'en-ca', 'en-au', 'en-in', 'en-nz'].includes(lc)) return 'en';

  // Portuguese
  if (lc === 'pt-br') return 'pt-br';
  if (lc === 'pt-pt' || lc === 'pt') return 'pt';

  // Spanish variants → 'es'
  if (['es', 'es-es', 'es-mx', 'es-ar', 'es-cl'].includes(lc)) return 'es';

  // Chinese
  if (lc === 'zh-cn') return 'zh-cn';
  if (lc === 'zh-tw') return 'zh-tw';

  // Others 1:1
  if (['fr-fr', 'fr'].includes(lc)) return 'fr';
  if (['de-de', 'de'].includes(lc)) return 'de';
  if (['it-it', 'it'].includes(lc)) return 'it';
  if (['ja-jp', 'ja'].includes(lc)) return 'ja';

  // Fallback
  return 'en';
}
